import { Controller } from "@hotwired/stimulus"

const BINANCE_WS_URL = "wss://stream.binance.com:9443/ws"

export default class extends Controller {
  static values = {
    thresholdPrice: Number
  }

  static targets = ["container", "interval", "chartType"]

  connect() {
    this.currentInterval = "5m"
    this.currentChartType = "candles"
    this.chart = null
    this.series = null
    this.thresholdLine = null
    this.ws = null
    this.wsReconnectTimer = null
    this._liveIndicator = null
    this._lastData = []

    this.loadLibrary()

    this._onThemeChanged = () => this.applyTheme()
    document.addEventListener("theme-changed", this._onThemeChanged)

    this._resizeObserver = new ResizeObserver(() => {
      if (this.chart) {
        this.chart.applyOptions({ width: this.containerTarget.clientWidth })
      }
    })
    this._resizeObserver.observe(this.containerTarget)
  }

  disconnect() {
    this.stopRefresh()
    this.closeWebSocket()
    document.removeEventListener("theme-changed", this._onThemeChanged)
    if (this._resizeObserver) this._resizeObserver.disconnect()
    if (this.chart) {
      this.chart.remove()
      this.chart = null
    }
  }

  async loadLibrary() {
    const lc = await import("lightweight-charts")
    this.lc = lc
    this.createChart()
    this.fetchData()
  }

  createChart() {
    if (this.chart) {
      this.chart.remove()
      this.chart = null
      this.series = null
      this.thresholdLine = null
    }

    const isDark = document.documentElement.getAttribute("data-theme") === "dark"
    const options = this.chartOptions(isDark)

    this.chart = this.lc.createChart(this.containerTarget, options)

    if (this.currentChartType === "line") {
      this.series = this.chart.addSeries(this.lc.AreaSeries, {
        lineColor: "#3B82F6",
        topColor: "rgba(59,130,246,0.4)",
        bottomColor: "rgba(59,130,246,0.04)",
        lineWidth: 2,
        crosshairMarkerRadius: 4,
        priceLineVisible: true,
        lastValueVisible: true
      })
    } else {
      this.series = this.chart.addSeries(this.lc.CandlestickSeries, {
        upColor: "#22C55E",
        downColor: "#EF4444",
        borderUpColor: "#22C55E",
        borderDownColor: "#EF4444",
        wickUpColor: "#22C55E",
        wickDownColor: "#EF4444"
      })
    }

    if (this.thresholdPriceValue > 0) {
      this.thresholdLine = this.series.createPriceLine({
        price: this.thresholdPriceValue,
        color: "#F59E0B",
        lineWidth: 2,
        lineStyle: this.lc.LineStyle.Dashed,
        axisLabelVisible: true,
        title: "Target"
      })
    }

    this.chart.timeScale().fitContent()
  }

  chartOptions(isDark) {
    return {
      width: this.containerTarget.clientWidth,
      height: window.innerWidth < 768 ? 280 : 400,
      layout: {
        background: { color: isDark ? "#1E1E1E" : "#FFFFFF" },
        textColor: isDark ? "#A0A0A0" : "#666666"
      },
      grid: {
        vertLines: { color: isDark ? "#2A2A2A" : "#F0F0F0" },
        horzLines: { color: isDark ? "#2A2A2A" : "#F0F0F0" }
      },
      crosshair: { mode: this.lc?.CrosshairMode?.Normal || 0 },
      rightPriceScale: {
        borderColor: isDark ? "#333333" : "#D0D0D0",
        scaleMargins: { top: 0.1, bottom: 0.1 }
      },
      timeScale: {
        borderColor: isDark ? "#333333" : "#D0D0D0",
        timeVisible: true,
        secondsVisible: this.currentChartType === "line"
      }
    }
  }

  applyTheme() {
    if (!this.chart) return
    const isDark = document.documentElement.getAttribute("data-theme") === "dark"
    this.chart.applyOptions(this.chartOptions(isDark))
  }

  async fetchData() {
    try {
      const resp = await fetch(`/api/btc_klines?interval=${this.currentInterval}&limit=200`)
      if (!resp.ok) return

      const data = await resp.json()
      this._lastData = data

      if (this.series && data.length) {
        this.setSeriesData(data)
        this.chart.timeScale().fitContent()
      }

      this.connectWebSocket()
    } catch (e) {
      console.error("Price chart fetch error:", e)
      this.scheduleRefresh()
    }
  }

  setSeriesData(data) {
    if (this.currentChartType === "line") {
      this.series.setData(data.map(d => ({ time: d.time, value: d.close })))
    } else {
      this.series.setData(data)
    }
  }

  // --- Chart Type Toggle ---

  selectChartType(event) {
    const type = event.currentTarget.dataset.chartType
    if (type === this.currentChartType) return

    this.currentChartType = type
    this.chartTypeTargets.forEach(btn => {
      btn.classList.toggle("price-chart__type--active", btn.dataset.chartType === type)
    })

    this.createChart()
    if (this._lastData.length) {
      this.setSeriesData(this._lastData)
      this.chart.timeScale().fitContent()
    }
  }

  // --- Binance WebSocket ---

  connectWebSocket() {
    this.closeWebSocket()

    const stream = `btcusdt@kline_${this.currentInterval}`
    const url = `${BINANCE_WS_URL}/${stream}`

    try {
      this.ws = new WebSocket(url)

      this.ws.onopen = () => {
        this.showLiveIndicator()
      }

      this.ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data)
          if (msg.k) {
            const time = Math.floor(msg.k.t / 1000)
            const close = parseFloat(msg.k.c)

            if (this.currentChartType === "line") {
              if (this.series) {
                this.series.update({ time, value: close })
              }
            } else {
              const candle = {
                time,
                open: parseFloat(msg.k.o),
                high: parseFloat(msg.k.h),
                low: parseFloat(msg.k.l),
                close
              }
              if (this.series) {
                this.series.update(candle)
              }
            }
          }
        } catch (e) {
          // Ignore malformed messages
        }
      }

      this.ws.onclose = () => {
        this.ws = null
        this.hideLiveIndicator()
        this.scheduleReconnect()
      }

      this.ws.onerror = () => {
        if (this.ws) {
          this.ws.close()
        }
      }
    } catch (e) {
      console.error("WebSocket connection error:", e)
      this.hideLiveIndicator()
      this.scheduleRefresh()
    }
  }

  closeWebSocket() {
    if (this.wsReconnectTimer) {
      clearTimeout(this.wsReconnectTimer)
      this.wsReconnectTimer = null
    }
    if (this.ws) {
      this.ws.onclose = null
      this.ws.onerror = null
      this.ws.onmessage = null
      this.ws.close()
      this.ws = null
    }
    this.hideLiveIndicator()
  }

  scheduleReconnect() {
    if (this.wsReconnectTimer) return
    this.wsReconnectTimer = setTimeout(() => {
      this.wsReconnectTimer = null
      this.connectWebSocket()
    }, 3000)
  }

  // --- LIVE Indicator ---

  showLiveIndicator() {
    if (this._liveIndicator) return
    const title = this.element.querySelector(".price-chart__title")
    if (!title) return
    this._liveIndicator = document.createElement("span")
    this._liveIndicator.className = "live-indicator"
    this._liveIndicator.innerHTML = '<span class="live-indicator__dot"></span> LIVE'
    title.appendChild(this._liveIndicator)
  }

  hideLiveIndicator() {
    if (this._liveIndicator) {
      this._liveIndicator.remove()
      this._liveIndicator = null
    }
  }

  selectInterval(event) {
    const interval = event.currentTarget.dataset.interval
    if (interval === this.currentInterval) return

    this.currentInterval = interval
    this.intervalTargets.forEach(btn => {
      btn.classList.toggle("price-chart__interval--active", btn.dataset.interval === interval)
    })

    this.stopRefresh()
    this.fetchData()
  }

  scheduleRefresh() {
    this.stopRefresh()
    const delays = { "1m": 15000, "5m": 30000, "15m": 60000, "1h": 120000, "4h": 300000, "1d": 600000 }
    this.refreshTimer = setTimeout(() => this.fetchData(), delays[this.currentInterval] || 60000)
  }

  stopRefresh() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }
  }
}
